import { Gumbo, Player, Venue } from "../../lib/types/statsApi";

//@ts-ignore
import GameDataPatch from "@gameday/game-data-patch";
import { Context, PoserMode } from "../context";
import StatsApi from "../../api/StatsApi";
import { toEpoch } from "../../lib/misc/util";
import flatMap from "lodash/flatMap";

export class GameStateManager {
  #gameState?: Gumbo;

  private prevGumboTimestamp?: string;
  private patcher: GameDataPatch;

  private prevTReal?: number;
  private updateSeconds: number;

  private updating = false;
  private fetchedOnce = false;

  constructor(updateSeconds = 5) {
    this.#gameState = undefined;
    this.prevGumboTimestamp = undefined;
    this.patcher = undefined;

    this.updateSeconds = updateSeconds;
  }

  async update(context: Context): Promise<Gumbo | undefined> {
    const { callbacks, clock, options } = context;

    let gameData: Gumbo | undefined = undefined;

    // TODO: make more general system that can update components on given dT
    if (
      this.updating ||
      (this.prevTReal != undefined &&
        Math.abs(this.prevTReal - clock.real) < this.updateSeconds)
    ) {
      return;
    }

    const timecode = timeInMsToGumboTimecode(clock.realMs);
    this.updating = true;

    // Don't diffpatch if in PLAY mode, grab gumbo/gamestate from timestamp of requested play
    if (
      this.prevGumboTimestamp !== undefined &&
      timecode !== undefined &&
      options.playbackMode !== PoserMode.PLAY
    ) {
      gameData = await StatsApi.fetchDiffPatch(
        options.gamePk,
        this.prevGumboTimestamp,
        timecode
      ).then((json) => {
        if (Array.isArray(json)) {
          let patchedData = undefined;
          json
            .filter((patchable) => patchable.diff.length > 0)
            .forEach((diff) => {
              // If it's an array of diffs, patch those diffs onto gumbo object
              patchedData = this.patcher.patch(diff).data;
            });
          if (patchedData != undefined) {
            callbacks.onGumboChange(
              // This typing is ugly, but the diff patching library doesn't give us any types.
              patchedData as unknown as Gumbo
            );
          }
          return patchedData;
        } else if (json.metaData.timeStamp !== this.prevGumboTimestamp) {
          // otherwise, it's a full update, set new gumbo object if the timestamp is different from our previous update
          callbacks.onGumboChange(json);
          return json;
        }
      });
    } else if (this.fetchedOnce === false) {
      gameData = await StatsApi.fetchGame(options.gamePk, timecode).then(
        (json) => {
          callbacks.onGumboChange(json);
          return json;
        }
      );
    }

    if (gameData) {
      this.#gameState = gameData;
      this.prevGumboTimestamp = gameData.metaData.timeStamp;
      this.createDiffPatcher(gameData);
    }

    // playId requested? Set the playLimits to play's startTime and endTime if they haven't already been set
    if (
      options.playbackMode === PoserMode.PLAY &&
      options.playId &&
      context.clock.playLimits === undefined
    ) {
      context.gameState.setStartAndEndTimeForPlayId(options.playId, context);
    }

    this.prevTReal = clock.real;
    this.updating = false;
    this.fetchedOnce = timecode !== undefined;
  }

  getPlayerByPositionId(
    positionId: number,
    context: Context
  ): Player | undefined {
    const currentPlay = this.getCurrentPlay();
    const linescore = this.#gameState?.liveData.linescore;

    const intraInning = this.inningOngoing();

    const positionMap: { [key: number]: number | undefined } = {
      1: intraInning
        ? linescore?.defense?.pitcher?.id
        : currentPlay?.matchup?.pitcher?.id,
      2: linescore?.defense?.catcher?.id,
      3: linescore?.defense?.first?.id,
      4: linescore?.defense?.second?.id,
      5: linescore?.defense?.third?.id,
      6: linescore?.defense?.shortstop?.id,
      7: linescore?.defense?.left?.id,
      8: linescore?.defense?.center?.id,
      9: linescore?.defense?.right?.id,
      10: intraInning
        ? linescore?.offense?.batter?.id
        : currentPlay?.matchup?.batter?.id,
      11: intraInning
        ? linescore?.offense?.first?.id
        : currentPlay?.matchup?.postOnFirst?.id,
      12: intraInning
        ? linescore?.offense?.second?.id
        : currentPlay?.matchup?.postOnSecond?.id,
      13: intraInning
        ? linescore?.offense?.third?.id
        : currentPlay?.matchup?.postOnThird?.id,
    };

    const id = positionMap[positionId];

    const teams = context.teamAndPlayerInfo.getTeams();

    return id != undefined
      ? teams?.away.players["ID" + id] ?? teams?.home.players["ID" + id]
      : undefined;
  }

  inningOngoing(): boolean {
    const linescore = this.#gameState?.liveData.linescore;
    const inningState = linescore?.inningState.toLowerCase() ?? "end";
    return !["middle", "end"].includes(inningState);
  }

  getHalfInningState() {
    return this.#gameState?.liveData.linescore.inningHalf;
  }

  getCurrentInningState() {
    const inningNumber = this.#gameState?.liveData.linescore.currentInning;
    const halfInningState = this.getHalfInningState();
    return inningNumber && halfInningState
      ? halfInningState + " " + halfInningState
      : "";
  }

  getCurrentPlay() {
    return this.#gameState?.liveData.plays.currentPlay;
  }

  isCurrentPlayActive() {
    return this.#gameState?.liveData.plays.currentPlay.about.isComplete;
  }

  getCurrentOuts() {
    return this.#gameState?.liveData.plays.currentPlay.count.outs;
  }

  getBattingTeam(): string | undefined {
    return this.getTeamAbbrevationForId(
      this.#gameState?.liveData.linescore.offense.team?.id
    );
  }

  getFieldingTeam(): string | undefined {
    return this.getTeamAbbrevationForId(
      this.#gameState?.liveData.linescore.defense.team?.id
    );
  }

  setStartAndEndTimeForPlayId(playId: string, context: Context) {
    const event = flatMap(
      this.#gameState?.liveData.plays.allPlays,
      (play) => play.playEvents
    ).find((event) => event.playId === playId);

    if (event !== undefined) {
      context.clock.playLimits = [
        toEpoch(event.startTime) / 1000,
        toEpoch(event.endTime) / 1000,
      ];
    } else {
      console.error(`unable to find play ${playId}`);
    }
  }

  getTeamAbbrevationForId(id?: number): string | undefined {
    if (id == undefined) {
      return undefined;
    }
    const teams = this.#gameState?.gameData.teams;
    return teams?.away.id === id
      ? teams?.away.abbreviation
      : teams?.home.abbreviation;
  }

  getVenueUTCOffset(): number | undefined {
    return this.#gameState?.gameData.venue.timeZone.offset;
  }

  getVenue(): Venue | undefined {
    return this.#gameState?.gameData.venue;
  }

  private createDiffPatcher(gameState: Gumbo) {
    this.patcher = new GameDataPatch(gameState);
  }
}

export function timeInMsToGumboTimecode(epochMs: number): string | undefined {
  if (epochMs === 0) {
    // Clock hasn't been set yet? Just grab a timecode-less gumbo, which represents the current state of the game.
    return undefined;
  }
  const currentDateTime = new Date(epochMs);
  return `${currentDateTime.getUTCFullYear()}${(
    currentDateTime.getUTCMonth() + 1
  )
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCDate()
    .toString()
    .padStart(2, "0")}_${currentDateTime
    .getUTCHours()
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCMinutes()
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCSeconds()
    .toString()
    .padStart(2, "0")}`; //"YYYMMdd_HHmmss"
}
